// const crypto = require('crypto');

const ALGORITHM = "aes-256-gcm";
const PBKDF2_HASH = "SHA-256";
const AUTH_TAG_LENGTH = 16;
const PBKDF2_ITERATIONS = 1000;
const IV_LENGTH = 12;

const IsNotNull = (s) => {
  return s ? true : false
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary_string = window.atob(base64);
  const len = binary_string.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}

function Encryption(secret, options) {
  if (!secret || typeof secret !== 'string') {
    throw new Error('secret must be a non-0-length string');
  }
  if (IsNotNull(options)) {
    if (!IsNotNull(options.saltString)) {
      if (!options.saltString || typeof options.saltString !== 'string') {
        throw new Error('salt must be a non-0-length string');
      }
    }
  }

  let saltString = '';
  let pbkdf2Iterations = PBKDF2_ITERATIONS;

  if (options) {
    if (options.pbkdf2Iterations) {
      pbkdf2Iterations = options.pbkdf2Iterations;
    }

    if (options.saltString) {
      saltString = options.saltString;
    }
  }

  async function getKey(salt) {
    const passwordBuffer = new TextEncoder().encode(secret);
    const saltBuffer = new TextEncoder().encode(salt);

    const keyMaterial = await window.crypto.subtle.importKey(
      "raw",
      passwordBuffer,
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
    );

    return window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: saltBuffer,
        iterations: pbkdf2Iterations,
        hash: PBKDF2_HASH,
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  this.encrypt = async function encrypt(value) {
    if (!IsNotNull(value)) {
      throw new Error('value must not be null or undefined');
    }

    const key = await getKey(saltString);
    const encodedData = new TextEncoder().encode(String(value));

    // 1. Generate IV (12 bytes)
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // 2. Encrypt (output is Ciphertext + 16-byte Tag)
    const cipherTextWithTagBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv },
      key,
      encodedData
    );

    // 3. Combine Binary Data (IV + Ciphertext + Tag)
    // Create a new array that can hold IV (12 bytes) + Ciphertext + Tag
    const combinedLength = iv.byteLength + cipherTextWithTagBuffer.byteLength;
    const combinedArray = new Uint8Array(combinedLength);

    // Copy IV first (first 12 bytes)
    combinedArray.set(iv, 0);

    // Copy Ciphertext + Tag immediately after the IV
    combinedArray.set(new Uint8Array(cipherTextWithTagBuffer), iv.byteLength);

    // 4. Encode the entire combined binary block to a Base64 string
    const combinedBase64String = arrayBufferToBase64(combinedArray.buffer);

    return combinedBase64String
  }

  this.decrypt = async function decrypt(valueBase64) {
    if (!IsNotNull(valueBase64) || typeof valueBase64 !== 'string') {
      throw new Error('valueBase64 must be a non-0-length string');
    }

    // NOTE: Using 'saltString' from the closure, consistent with 'encrypt'
    const key = await getKey(saltString);

    // 1. Decode the Base64 string into a combined binary buffer
    const combinedBuffer = base64ToArrayBuffer(valueBase64);
    const combinedArray = new Uint8Array(combinedBuffer);

    // 2. Separate the components
    if (combinedArray.byteLength < IV_LENGTH + AUTH_TAG_LENGTH) {
      throw new Error('Ciphertext is too short to contain IV and Authentication Tag');
    }

    // IV is the first 12 bytes
    const iv = combinedArray.slice(0, IV_LENGTH);

    // Ciphertext + Tag is the rest
    const cipherTextWithTag = combinedArray.slice(IV_LENGTH);

    // 3. Decrypt (the function handles separating Ciphertext from the Tag internally)
    let decryptedBuffer;
    try {
      decryptedBuffer = await window.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv },
        key,
        cipherTextWithTag
      );
    } catch (e) {
      // This catch is vital for integrity check failure (invalid tag)
      throw new Error('Decryption failed. Data may be corrupted or key is incorrect. Error: ' + e.message);
    }

    // 4. Decode the resulting plaintext buffer into a string
    const decryptedString = new TextDecoder().decode(decryptedBuffer);

    return decryptedString;
  }

}

module.exports = Encryption;
